import { Button } from '@platform/ui-library';
import { FC, useEffect, useState } from 'react';
import {
  FileUploaderModal,
  FormField,
  InputAcceptType
} from '@app/form_components';
import { useDesign } from '@app/hooks';
import {
  CsvDropdown,
  IntentLabelTable,
  ObjectDropdown
} from '@app/page-components';
import { Design, NodeType } from '@app/types';
import {
  createCsv,
  createUniqueValue,
  getIntentLabelColumns,
  removeDuplicates
} from '@app/utilities';
import Utility from '@app/utilities/Utility';

const createIntentLabelCsv = (serialized: Design) => {
  return createCsv({
    columns: getIntentLabelColumns(serialized),
    items: removeDuplicates({
      items: serialized.components.intents,
      key: 'name'
    })
  });
};

/**
 * Settings page for HAU configuration.
 *
 * @note This is not using React router since I did not have time to switch it over.
 */
export const SettingsHauPage: FC = () => {
  const appManager = window.apm;
  const serialized = useDesign();
  const { csvData, rootSettings } = serialized;
  const [isIntentLabelModalOpen, setIsIntentLabelModalOpen] = useState(false);
  // Stored in state in addition to using @see useDesign to speed up the UI.
  const [disambiguationPromptId, setDisambiguationPromptId] = useState<string>();
  const [csvIntentLabelTableId, setCsvIntentLabelTableId] = useState<string>();

  useEffect(() => {
    setDisambiguationPromptId(rootSettings.disambiguationPromptId);
  }, [rootSettings.disambiguationPromptId]);

  useEffect(() => {
    setCsvIntentLabelTableId(rootSettings.csvIntentLabelTableId);
  }, [rootSettings.csvIntentLabelTableId]);

  return (
    <div className="flex-column flex-gap">
      <FormField
        label="Disambiguation Prompt"
        tooltip="When N-best is enabled, this prompt asks the user which of three intents they mean; this can be overriden at the node level."
      >
        <ObjectDropdown
          onChange={(selectedId) => {
            const newId = selectedId ?? undefined;
            const { serialized } = appManager;
            const { rootSettings } = serialized;
            rootSettings.disambiguationPromptId = newId;
            setDisambiguationPromptId(newId);
            rootSettings.lastLocalChange = appManager.timestamp;
            appManager.setSerializedChanged();
          }}
          type={NodeType.prompt}
          value={disambiguationPromptId ?? ''}
        />
      </FormField>
      <div className="flex-gap">
        <FormField
          className="flex-grow"
          label="Intent Label CSV"
        >
          <CsvDropdown
            onChange={(selectedId) => {
              const newId = selectedId ?? undefined;
              const { serialized } = appManager;
              const { rootSettings } = serialized;
              rootSettings.csvIntentLabelTableId = newId;
              setCsvIntentLabelTableId(newId);
              rootSettings.lastLocalChange = appManager.timestamp;
              appManager.setSerializedChanged();
            }}
            value={csvIntentLabelTableId}
          />
        </FormField>
        <div className="flex-column flex-end">
          <Button
            onClick={() => {
              setIsIntentLabelModalOpen(true);
            }}
            theme='default'
          >
            Upload New CSV
          </Button>
        </div>
      </div>
      <FileUploaderModal
        accept={InputAcceptType.csv}
        isOpen={isIntentLabelModalOpen}
        labels={{ header: 'Upload Intent Label CSV' }}
        template={{
          fileName: 'intent_labels.csv',
          text: () => {
            return createIntentLabelCsv(serialized);
          }
        }}
        onComplete={(file) => {
          setIsIntentLabelModalOpen(false);
          if (!file) {
            return;
          }
          const { fileName, text } = file;
          const { serialized, timestamp } = appManager;
          const id = Utility.uuid4();
          const name = createUniqueValue({
            existing: csvData.map(({ name }) => {
              return name;
            }),
            generate: (seed) => {
              const addOn = (seed === 0) ? '' : `_${seed}`;
              return `${fileName.replace(/\.csv$/, '')}${addOn}.csv`;
            }
          });
          serialized.csvData.push({
            createdTime: timestamp,
            id,
            lastLocalChange: timestamp,
            lastSavedChange: timestamp,
            name,
            value: text
          });
          serialized.rootSettings.csvIntentLabelTableId = id;
          serialized.rootSettings.lastLocalChange = timestamp;
          setCsvIntentLabelTableId(id);
          appManager.setSerializedChanged();
          appManager.reactDOMComponents.render({
            $el: $('#settings'),
            name: 'SettingWidget',
            props: {
              // This prop change forces the ItemList to re-render...
              csvDataKey: id,
              serialized
            }
          });
        }}
      />
      <IntentLabelTable />
    </div>
  );
};
